'use client';

import React, { useEffect, useRef, useState } from 'react';
import { useRouter } from 'next/navigation';

import { Typography } from 'lighthouse';

import { BatteryChoiceCard } from '@/widgets/BatteryChoiceCard';
import { BatteryChoiceCardLoading } from '@/widgets/BatteryChoiceCard/BatteryChoiceCardLoading';
import { InfiniteScrollList } from '@/widgets/InfiniteScrollList';
import { InfiniteScrollListRef } from '@/widgets/InfiniteScrollList/models';
import LoginModal from '@/widgets/LoginModal';
import { SelectableProductListProps } from '@/widgets/SelectableProductList/models';

import { getBatteryCompatibilityBadge } from '@/utilities/batteryProduct';
import { isUserLogin } from '@/utilities/CheckUserIsLogin';
import {
    mapBatteryRouteToDataLayerVehicle,
    setDataLayerContext,
    splitVehicleName,
    trackBatteryIdentityCheck,
    trackBatteryOtpSubFunnel,
    trackBatterySelection
} from '@/utilities/dataLayer';

import { useCityStore } from '@/stores/city/useCityStore';

import { BATTERY_PRODUCT_HIDDEN_DISCOUNT_PERCENTAGE, isSuggestedBattery, vehicleTypeTitle } from '@/constants/batteryProduct';
import { PRODUCTS_SORT_AVAILABLE_DESC_AND_SELLING_PRICE_ASC_ID } from '@/constants/common';
import { AUTH_METHODS } from '@/constants/dataLayer';
import { batteryRequest, requestReservation } from '@/constants/routes';

import { setBatteryOrderService } from '@/client/services/core/batteryOrder/methods';
import { getProductsBatteryByVehicleFiltersService } from '@/client/services/core/productBattery/methods';
import { ProductBatteryType } from '@/server/services/core/productBattery/models';
import axios from 'axios';
import { cornbar } from 'cornbar';
import { TickCircle } from 'iconsax-react';

const requestGridClassName =
    'flex px-2 gap-4 flex-col [@container_(min-width:768px)]:grid [@container_(min-width:768px)]:grid-cols-2 [@container_(min-width:768px)]:gap-x-4';
const catalogGridClassName = 'grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4';
const requestProductClassName = 'h-full w-full min-w-full [@container_(min-width:768px)]:min-w-[360px]';
const catalogProductClassName = 'h-full w-full';

export const SelectableProductList = ({ vehicleModel, vehicleType, filterOptions, layout = 'request' }: SelectableProductListProps) => {
    const isCatalogLayout = layout === 'catalog';
    const gridClassName = isCatalogLayout ? catalogGridClassName : requestGridClassName;
    const productClassName = isCatalogLayout ? catalogProductClassName : requestProductClassName;

    const dataLayerVehicle = mapBatteryRouteToDataLayerVehicle(vehicleType);
    const getVehicleBrandModel = () => splitVehicleName(vehicleModel?.name || '');

    const trackBatterySelectionAndContext = (product: ProductBatteryType) => {
        const { brand, model } = getVehicleBrandModel();

        trackBatterySelection({
            vehicle: dataLayerVehicle,
            brand,
            model,
            itemId: product.uuid,
            itemName: product.title,
            batteryBrand: product.brand.name,
            price: product.sellingPrice
        });
        setDataLayerContext({
            batteryVehicle: dataLayerVehicle,
            batteryBrand: brand,
            batteryModel: model,
            batteryItemId: product.uuid,
            batteryItemName: product.title,
            batteryItemBrand: product.brand.name,
            batteryItemPrice: product.sellingPrice
        });
    };
    const router = useRouter();
    const infiniteScrollRef = useRef<InfiniteScrollListRef>(null);
    const cityId = useCityStore((state) => state.cityId);

    const [products, setProducts] = useState<ProductBatteryType[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const [isLastPage, setIsLastPage] = useState(false);
    const [isSubmitLoading, setIsSubmitLoading] = useState(false);
    const [selectedProductUuid, setSelectedProductUuid] = useState('');
    const [showLoginModal, setShowLoginModal] = useState(false);

    const fetchProducts = async (page: number) => {
        if (!vehicleModel) {
            setIsLastPage(true);
            return [];
        }

        try {
            const res = await getProductsBatteryByVehicleFiltersService({
                poles: filterOptions?.poles,
                heights: filterOptions?.heights,
                amperes: filterOptions?.amperes,
                categoryIds: filterOptions?.categoryIds ?? [0],
                page,
                cityId,
                size: isCatalogLayout ? 12 : 10,
                sortOption: PRODUCTS_SORT_AVAILABLE_DESC_AND_SELLING_PRICE_ASC_ID,
                sort: ['ampere,asc']
            });

            if (useCityStore.getState().cityId !== cityId) return [];

            const newItems = res.data.content || [];
            setIsLastPage(Boolean(res.data.last) || newItems.length === 0);
            if (page === 0) setProducts(newItems);
            return newItems;
        } catch {
            if (useCityStore.getState().cityId === cityId) setIsLastPage(true);
            return [];
        }
    };

    useEffect(() => {
        if (!vehicleModel) return;

        let isIgnored = false;
        setIsLoading(true);
        setSelectedProductUuid('');
        void fetchProducts(0).then((initialProducts) => {
            if (isIgnored) return;
            setProducts(initialProducts);
            infiniteScrollRef.current?.resetPaginate();
            setIsLoading(false);
        });

        return () => {
            isIgnored = true;
        };
    }, [vehicleModel, filterOptions?.categoryIds, cityId]);

    const handleNextStep = async (uuid: string, options?: { fromLogin?: boolean }) => {
        setIsSubmitLoading(true);
        const product = products.find((item) => item.uuid === uuid) || null;
        const fromLogin = Boolean(options?.fromLogin);
        const authMethod = fromLogin ? AUTH_METHODS.OTP_VERIFIED : AUTH_METHODS.ALREADY_LOGGED_IN;

        if (!isUserLogin()) {
            if (product) {
                trackBatterySelectionAndContext(product);
            }
            setSelectedProductUuid(uuid);
            setShowLoginModal(true);
            setIsSubmitLoading(false);
            return;
        }

        try {
            if (!fromLogin && product) {
                trackBatterySelectionAndContext(product);
            }

            const { brand, model } = getVehicleBrandModel();
            trackBatteryIdentityCheck({
                vehicle: dataLayerVehicle,
                brand,
                model,
                authMethod
            });

            await setBatteryOrderService({ productUuid: uuid, vehicleId: vehicleModel!.id!, cityId }).then((res) => {
                if (res.status === 201) {
                    setShowLoginModal(false);
                    void router.push(`/${vehicleType}${batteryRequest}/${res.data.content.uuid}${requestReservation}`);
                } else {
                    cornbar.show({
                        title: 'خطا رخ داد',
                        description: 'درخواست شما ثبت نشد. لطفا مجدد تلاش کنید!',
                        variant: 'error'
                    });
                }
            });
        } catch (error) {
            if (axios.isAxiosError(error) && (error.response?.status === 401 || error.response?.status === 403)) {
                setShowLoginModal(true);
            }
            setIsSubmitLoading(false);
        }
    };

    const handleSelectProduct = (uuid: string) => {
        void handleNextStep(uuid);
    };

    const handleUserLogin = () => {
        setShowLoginModal(false);
        if (selectedProductUuid) {
            void handleNextStep(selectedProductUuid, { fromLogin: true });
        }
    };

    const loginAnalytics = {
        onPopupShown: () => {
            const { brand, model } = getVehicleBrandModel();
            trackBatteryOtpSubFunnel({ vehicle: dataLayerVehicle, step: 'popup_shown', brand, model });
        },
        onOtpCodeRequested: () => {
            const { brand, model } = getVehicleBrandModel();
            trackBatteryOtpSubFunnel({ vehicle: dataLayerVehicle, step: 'code_requested', brand, model });
        },
        onOtpVerified: () => {
            const { brand, model } = getVehicleBrandModel();
            trackBatteryOtpSubFunnel({ vehicle: dataLayerVehicle, step: 'verified', brand, model });
        }
    };

    const handleCloseLoginModal = () => {
        setShowLoginModal(false);
        setIsSubmitLoading(false);
    };

    if (isLoading) {
        return (
            <div className={gridClassName}>
                <BatteryChoiceCardLoading />
            </div>
        );
    }

    const getCompatibilityBadge = (product: ProductBatteryType) =>
        getBatteryCompatibilityBadge(
            vehicleModel?.params?.batteryCompatibility?.amperes?.[0] === product?.ampere,
            vehicleTypeTitle[vehicleType as keyof typeof vehicleTypeTitle]
        );

    return (
        <>
            <InfiniteScrollList<ProductBatteryType>
                ref={infiniteScrollRef}
                isLastPage={isLastPage}
                fetchData={fetchProducts}
                renderRow={(product) =>
                    product.discountPercentage !== BATTERY_PRODUCT_HIDDEN_DISCOUNT_PERCENTAGE ? (
                        <BatteryChoiceCard
                            key={product.uuid}
                            product={product}
                            className={productClassName}
                            badge={
                                vehicleModel?.params?.batteryCompatibility?.amperes?.length && product.available
                                    ? getCompatibilityBadge(product)
                                    : undefined
                            }
                            isSuggested={!!isSuggestedBattery(product)}
                            isLoading={isSubmitLoading}
                            onSelect={handleSelectProduct}
                        />
                    ) : (
                        <></>
                    )
                }
                defaultItems={products}
                endMessage={
                    products.length ? (
                        <div
                            className={`mt-6 flex items-center justify-center gap-3 rounded-lg border border-dashed border-primary/15 bg-sky-50/70
                                px-4 py-5 ${isCatalogLayout ? '' : 'mx-2'}`}>
                            <span className='flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-primary/10 text-primary'>
                                <TickCircle size='20' variant='Bold' color='currentColor' />
                            </span>
                            <div className='text-right'>
                                <Typography variant='subtitle2' component='p' color='blackHighEmphasis' className='font-bold'>
                                    همه محصولات نمایش داده شد
                                </Typography>
                                <Typography variant='caption' component='p' color='blackMediumEmphasis'>
                                    مورد بیشتری برای نمایش وجود ندارد
                                </Typography>
                            </div>
                        </div>
                    ) : null
                }
                containerProps={{
                    className: gridClassName
                }}
                infiniteScrollProps={{
                    scrollThreshold: 0.4,
                    loader: (
                        <div
                            className={
                                isCatalogLayout
                                    ? `mt-4 ${catalogGridClassName}`
                                    : `mt-2 grid grid-cols-1 gap-4 px-2 pb-2 [@container_(min-width:768px)]:grid-cols-2
                                        [@container_(min-width:768px)]:gap-x-4`
                            }>
                            <BatteryChoiceCardLoading />
                        </div>
                    ),
                    className: isCatalogLayout
                        ? 'w-full'
                        : 'flex w-full flex-col [@container_(min-width:768px)]:gap-y-3 [@container_(min-width:768px)]:gap-x-4'
                }}
            />

            {showLoginModal && <LoginModal onUserLogin={handleUserLogin} onClose={handleCloseLoginModal} analytics={loginAnalytics} />}
        </>
    );
};
